import { useState, type FormEvent } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError } from '../../api/client'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'

// DIS-39 / DIS-62: Settings › Workspace (admin): name and the @channel default.
export const Route = createFileRoute('/_app/settings/workspace')({
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' })
  },
  component: WorkspacePage,
})

export const workspaceQuery = queryOptions({
  queryKey: ['workspace'],
  queryFn: async () => {
    const { data, response } = await api.GET('/workspace')
    if (!data) throw new Error(`GET /workspace failed: ${response.status}`)
    return data
  },
})

function WorkspacePage() {
  const qc = useQueryClient()
  const ws = useQuery(workspaceQuery)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: async (body: { name: string; mention_all_policy: 'everyone' | 'admins' | 'nobody'; retention_days: number; retention_keep_pinned: boolean }) => {
      const { data, error: err, response } = await api.PATCH('/admin/workspace', { body })
      if (!data) throw new Error((err as ApiError | undefined)?.message ?? `failed (${response.status})`)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(workspaceQuery.queryKey, data)
      setError(null)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1500)
    },
    onError: (e: Error) => setError(e.message),
  })
  const [pending, setPending] = useState<{ name: string; mention_all_policy: 'everyone' | 'admins' | 'nobody'; retention_days: number; retention_keep_pinned: boolean } | null>(null)
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const body = {
      name: String(f.get('name') ?? '').trim(),
      mention_all_policy: String(f.get('mention_all_policy')) as 'everyone' | 'admins' | 'nobody',
      retention_days: Number(f.get('retention_days') || 0),
      retention_keep_pinned: f.get('retention_keep_pinned') === 'on',
    }
    const current = ws.data?.retention_days ?? 0
    // Destructive: enabling or shortening retention deletes history. Confirm first.
    if (body.retention_days > 0 && (current === 0 || body.retention_days < current)) {
      setPending(body)
      return
    }
    save.mutate(body)
  }
  if (!ws.data) return <div className="p-6 text-[13px] text-fg-muted">Loading…</div>
  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-lg font-semibold">Workspace</h1>
        <p className="text-[13px] text-fg-muted">Defaults every channel inherits unless it overrides them in Channel settings.</p>
      </div>
      <form key={ws.data.name + ws.data.mention_all_policy} onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-bg-muted p-4" data-testid="workspace-form">
        <label className="block text-[13px]">
          <span className="font-medium">Workspace name</span>
          <Input name="name" defaultValue={ws.data.name} required maxLength={80} className="mt-1" />
        </label>
        <label className="block text-[13px]">
          <span className="font-medium">Who can use @channel / @here</span>
          <select name="mention_all_policy" aria-label="Default mention policy" defaultValue={ws.data.mention_all_policy} className="mt-1 block h-8 w-full rounded-md border border-border bg-bg px-2 text-[13px]">
            <option value="everyone">Everyone</option>
            <option value="admins">Admins only</option>
            <option value="nobody">Nobody</option>
          </select>
        </label>
        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-1 text-[12px] font-medium text-fg-muted">Message retention</legend>
          <label className="block text-[13px]">
            <span className="font-medium">Delete messages older than</span>
            <span className="mt-1 flex items-center gap-2">
              <Input name="retention_days" type="number" min={0} max={3650} defaultValue={ws.data.retention_days ?? 0} aria-label="Retention days" className="w-28" />
              <span className="text-fg-muted">days (0 = keep forever)</span>
            </span>
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" name="retention_keep_pinned" className="accent-accent" defaultChecked={ws.data.retention_keep_pinned} />
            Keep pinned messages
          </label>
          <p className="text-[12px] text-fg-muted">
            Runs nightly and removes messages and their files permanently. Channels can override this in Channel settings.
            {ws.data.retention_last_run_at && ` Last run ${new Date(ws.data.retention_last_run_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.`}
          </p>
        </fieldset>
        {error && (
          <p role="alert" className="text-[13px] text-danger">
            {error}
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>Save</Button>
          {saved && <span className="text-[12px] text-success" data-testid="workspace-saved">Saved</span>}
        </div>
      </form>
      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        title="Delete old messages permanently?"
        description={`Messages older than ${pending?.retention_days ?? 0} days will be removed nightly, along with their files. This can't be undone.`}
        confirmLabel="Enable retention"
        onConfirm={() => {
          if (pending) save.mutate(pending)
          setPending(null)
        }}
      />
    </div>
  )
}
